import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  ReactNode,
} from 'react'
import queryString, { ParsedQuery } from 'query-string'
import { useRouter } from 'next/router'
import { toast } from 'react-hot-toast'
import PageLoader from '../components/PageLoader'

import supabase from 'lib/supabase-browser'
import { useUser, updateUser } from './db'
import { getFriendlyPlanId } from './prices'
import analytics from './analytics'
import Sentry from './sentry'
import { MAIL_GROUPS } from 'lib/mail-groups'
import { subscribe } from 'util/mailer'
import { useProjectStore } from 'store/useProjectStore'
import { useAcceptTeamInvite } from 'hooks/project_members'

// Types from supabase-js
import {
  User as SupabaseUser,
  AuthResponse,
  Provider,
} from '@supabase/supabase-js'
import { User } from '../lib/types/generated-types'
import { useCustomer } from 'hooks/customers'
import { SHUTDOWN_CANCELED_STATUS } from 'lib/shutdown'

declare global {
  interface Window {
    lastHash: ParsedQuery<string>
  }
}

/* -------------------------------------------------------------------------- */
/*                               CONFIG OPTIONS                               */
/* -------------------------------------------------------------------------- */

// Whether to merge extra user data from database into `auth.user`
const MERGE_DB_USER = true

// Whether to connect analytics session to `user.uid`
const ANALYTICS_IDENTIFY = true

/* -------------------------------------------------------------------------- */
/*                            EXTENDED USER TYPES                             */
/* -------------------------------------------------------------------------- */

interface ExtendedUser extends Omit<SupabaseUser, keyof User>, User {
  app_metadata: {
    provider?: string
    [key: string]: any
  }
  customers?: {
    stripePriceId?: string
    stripeSubscriptionStatus?: string
    [key: string]: any
  }
  uid?: string
  providers?: string[]
  planId?: string
  planIsActive?: boolean
  [key: string]: any
}

/* -------------------------------------------------------------------------- */
/*                               AUTH CONTEXT                                 */
/* -------------------------------------------------------------------------- */

interface AuthContextType {
  user: ExtendedUser | null | false
  signup: (args: {
    email: string
    password: string
    sendToTrial?: boolean
  }) => Promise<ExtendedUser>
  signin: (email: string, password: string) => Promise<ExtendedUser>
  signinWithProvider: (name: Provider) => Promise<never>
  signinWithMagicLink: (email: string) => Promise<AuthResponse>
  signout: () => Promise<void>
  sendPasswordResetEmail: (email: string) => Promise<{ error: Error | null }>
  confirmPasswordReset: (password: string) => Promise<{ error: Error | null }>
  updatePassword: (password: string) => Promise<{ error: Error | null }>
  updateProfile: (data: { email?: string; [key: string]: any }) => Promise<void>
}

const authContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = (): AuthContextType => {
  const context = useContext(authContext)
  if (!context) throw new Error('useAuth must be used within an AuthProvider')
  return context
}

/**
 * AuthProvider
 * - Wraps the entire application (e.g., in _app.tsx)
 * - Provides the auth object and methods to any descendant.
 */
export function AuthProvider({ children }) {
  const auth = useAuthProvider()
  return <authContext.Provider value={auth}>{children}</authContext.Provider>
}

/* -------------------------------------------------------------------------- */
/*                           INTERNAL HOOK: useAuthProvider                   */
/* -------------------------------------------------------------------------- */

/**
 * useAuthProvider
 * - Creates the auth object and handles state.
 * - Called from AuthProvider above.
 */
function useAuthProvider() {
  // `user` can be: null (loading), false (logged out), or ExtendedUser (logged in)
  const [user, setUser] = useState<ExtendedUser | null | false>(null)

  const projectStore = useProjectStore()
  const router = useRouter()
  const { mutateAsync: acceptTeamInvite } = useAcceptTeamInvite()
  const { team, project } = router.query

  // Merge extra user data from the database
  let finalUser = useMergeExtraData(user, { enabled: MERGE_DB_USER })
  // Format the user object with convenience fields
  finalUser = useFormatUser(finalUser)
  // Connect analytics session to user
  useIdentifyUser(finalUser, { enabled: ANALYTICS_IDENTIFY })

  // ------------------------------------------------------------------------
  // Team/Project invite acceptance
  // ------------------------------------------------------------------------
  const getProjectIds = (projectsParam: string | string[] | undefined) => {
    if (!projectsParam || typeof projectsParam !== 'string') return []
    try {
      return projectsParam.split(',').map((p) => parseInt(p, 10))
    } catch (error) {
      return []
    }
  }

  const handleAcceptTeamInvite = async (data: ExtendedUser) => {
    const teamId = parseInt(team as string, 10)
    if (!teamId) return
    try {
      await acceptTeamInvite({
        email: data.email ?? '',
        teamId,
        userId: data.id,
        projectIds: getProjectIds(project),
      })
      toast.success('Successfully joined team')
    } catch (error) {
      Sentry.captureException(error)
      console.error('Failed to join team:', error)
      toast.error('Failed to join team')
    }
  }

  // ------------------------------------------------------------------------
  // Auth callbacks
  // ------------------------------------------------------------------------
  const handleAuth = async (response: AuthResponse): Promise<ExtendedUser> => {
    const {
      data: { user: authUser },
    } = response
    if (!authUser) {
      throw new Error('No user returned from auth response.')
    }

    // If email is unconfirmed (signups) => throw error to show in UI
    if (!authUser.email_confirmed_at) {
      throw new Error(
        'Thanks for signing up! Please check your email to complete the process.'
      )
    }

    // Update local state
    setUser(authUser as ExtendedUser)
    return authUser as ExtendedUser
  }

  const signup = async ({
    email,
    password,
    sendToTrial = true,
  }: {
    email: string
    password: string
    sendToTrial?: boolean
  }): Promise<ExtendedUser> => {
    const response = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${process.env.NEXT_PUBLIC_BASE_URL}/setup/billing`,
      },
    })

    if (response.error) {
      Sentry.captureException(response.error)
      throw response.error
    }

    const createdUser = response.data.user as ExtendedUser
    if (!createdUser) {
      throw new Error('No user returned from signup.')
    }

    // Subscribe the user to a mailing list
    try {
      await subscribe({
        email,
        group: MAIL_GROUPS.registered_users,
      })
    } catch (error) {
      Sentry.captureException(error)
      console.error(error)
    }

    // If sending to a trial flow
    if (sendToTrial) {
      await updateUser(createdUser.id, {
        hasCompletedSetup: false,
        hasCreatedFirstProject: false,
        hasStartedTrial: false,
        hasUploadedFirstFile: false,
      })
      router.push('/setup/billing')
    } else {
      // Accept team invite if any
      await updateUser(createdUser.id, {
        hasCompletedSetup: true,
        hasCreatedFirstProject: true,
        hasStartedTrial: true,
        hasUploadedFirstFile: true,
      })
      await handleAcceptTeamInvite(createdUser)

      // If project(s) exist, redirect
      const projectIds = getProjectIds(project)
      if (projectIds.length > 0) {
        router.push(
          `/projects/${projectIds[0]}`
            ? `/projects/${projectIds[0]}`
            : '/dashboard'
        )
      }
    }

    return handleAuth(response)
  }

  const signin = async (
    email: string,
    password: string
  ): Promise<ExtendedUser> => {
    const response = await supabase.auth
      .signInWithPassword({
        email,
        password,
      })
      .then(handleError)
    return handleAuth(response)
  }

  const signinWithProvider = (name: Provider): Promise<never> => {
    return supabase.auth
      .signInWithOAuth({
        provider: name,
        options: {
          redirectTo: `${window.location.origin}/dashboard`,
        },
      })
      .then(handleError)
      .then(() => {
        // signInWithOAuth resolves immediately; keep returning a pending promise
        // so we never "finish" => user is redirected after successful OAuth
        return new Promise<never>(() => null)
      })
  }

  const signinWithMagicLink = async (email: string): Promise<AuthResponse> => {
    return supabase.auth
      .signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/dashboard`,
        },
      })
      .then(handleError)
  }

  const signout = async (): Promise<void> => {
    await supabase.auth
      .signOut({
        scope: 'local',
      })
      .then(handleError)
    projectStore.reset()
  }

  const sendPasswordResetEmail = async (email: string) => {
    return supabase.auth
      .resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/changepass`,
      })
      .then(handleError)
  }

  const confirmPasswordReset = async (password: string) => {
    return supabase.auth.updateUser({ password }).then(handleError)
  }

  const updatePassword = async (password: string) => {
    return supabase.auth.updateUser({ password }).then(handleError)
  }

  const updateProfile = async (data: {
    email?: string
    [key: string]: any
  }): Promise<void> => {
    if (!user || typeof user === 'boolean') {
      throw new Error('Cannot updateProfile: user is not authenticated.')
    }

    const { email, ...other } = data

    if (email && email !== user.email) {
      await supabase.auth.updateUser({ email }).then(handleError)
      throw new Error(
        'To complete this process click the confirmation links sent to your new and old email addresses.'
      )
    }

    if (Object.keys(other).length > 0) {
      await updateUser(user.id, other)
    }
  }

  // ------------------------------------------------------------------------
  // On mount, check for existing user session or set up event subscription
  // ------------------------------------------------------------------------
  useEffect(() => {
    // Grab hash from URL if coming from OAuth or Magic Link
    window.lastHash = queryString.parse(window.location.hash)

    // If no `access_token` in hash, attempt to restore from the cached session
    if (!window.lastHash.access_token) {
      supabase.auth.getSession().then(({ data }) => {
        const session = data.session
        if (session?.user) {
          setUser(session.user as ExtendedUser)
        } else {
          setUser(false)
        }
      })
    }

    // Subscribe to user changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        setUser(session.user as ExtendedUser)
      } else {
        setUser(false)
      }
    })

    // Unsubscribe on unmount
    return () => {
      subscription.unsubscribe()
    }
  }, [])

  // ------------------------------------------------------------------------
  // Return the auth object
  // ------------------------------------------------------------------------
  return {
    user: finalUser,
    signup,
    signin,
    signinWithProvider,
    signinWithMagicLink,
    signout,
    sendPasswordResetEmail,
    confirmPasswordReset,
    updatePassword,
    updateProfile,
  }
}

/* -------------------------------------------------------------------------- */
/*                        HOOKS: useFormatUser, useMergeExtraData,            */
/*                                useIdentifyUser                             */
/* -------------------------------------------------------------------------- */

/**
 * useFormatUser
 * - Add custom fields & formatting to the raw user object.
 */
function useFormatUser(user: ExtendedUser | null | false) {
  return useMemo<ExtendedUser | null | false>(() => {
    if (!user) return user

    // Supabase calls the provider "email" for password-based.
    let provider = user.app_metadata?.provider || 'password'
    if (provider === 'email') provider = 'password'
    const providers: string[] = [provider]

    return {
      ...user,
      uid: user.id,
      providers,
      ...(user.customers?.stripePriceId && {
        planId: getFriendlyPlanId(user.customers.stripePriceId),
      }),
      planIsActive: [
        'active',
        'trialing',
        'past_due',
        // Wind-down: plans cancelled during the shutdown stay usable until
        // the app goes offline on Dec 1, 2026
        SHUTDOWN_CANCELED_STATUS,
      ].includes(user.customers?.stripeSubscriptionStatus ?? ''),
    }
  }, [user])
}

/**
 * useMergeExtraData
 * - If enabled, merges DB user data with Supabase user object.
 * - If the DB data is null and user is present, we return `null` to keep in loading state
 *   until DB finishes populating.
 */
function useMergeExtraData(
  user: ExtendedUser | null | false,
  { enabled }: { enabled: boolean }
): ExtendedUser | null | false {
  const userId = user && typeof user !== 'boolean' ? user.id : null
  const { data, status, error } = useUser(enabled && userId ? userId : null)
  const { data: customerData } = useCustomer({ userId })

  return useMemo(() => {
    if (!enabled || !user) return user

    switch (status) {
      case 'success':
        if (data === null) return null
        // Convert null customers to undefined to match ExtendedUser type
        const mergedData = {
          ...user,
          ...data,
          customers: customerData || undefined,
        }
        return mergedData as ExtendedUser
      case 'error':
        throw new Error(`${(error as any)?.message}`)
      default:
        return null
    }
  }, [user, enabled, data, status, error, customerData])
}

/**
 * useIdentifyUser
 * - Connect analytics session to current user.
 */
function useIdentifyUser(
  user: ExtendedUser | null | false,
  { enabled }: { enabled: boolean }
) {
  useEffect(() => {
    if (user && typeof user !== 'boolean' && enabled) {
      analytics.identify(user.uid ?? '')
    }
  }, [user, enabled])
}

/* -------------------------------------------------------------------------- */
/*                      HOC: requireAuth - Protecting Routes                  */
/* -------------------------------------------------------------------------- */

interface RequireAuthHOCProps {
  // Add any extra props that your wrapped component might need
  [key: string]: unknown
}

/**
 * requireAuth
 * - A higher-order component (HOC) for requiring authentication
 * - If user is not signed in, redirect to login.
 */
export function requireAuth<P extends RequireAuthHOCProps>(
  Component: React.ComponentType<P>
) {
  return function RequireAuthHOC(props: P) {
    const auth = useAuth()
    const router = useRouter()

    useEffect(() => {
      if (auth.user === false) {
        router.replace('/login')
      }
    }, [auth, router])

    // Loading: either user is `null` (fetching) or `false` (just about to redirect)
    if (!auth.user) {
      return <PageLoader />
    }

    return <Component {...props} />
  }
}

/* -------------------------------------------------------------------------- */
/*                                HELPER FUNC                                 */
/* -------------------------------------------------------------------------- */

/**
 * handleError
 * - Throw error from auth response so it can be caught & displayed by UI
 */
function handleError<T>(response: T & { error?: Error }) {
  if (response.error) throw response.error
  return response
}
